import { useEffect, useMemo, useState } from 'react';
import type { GameAdapter } from './adapter';
import { LocalGame } from './local';
import { OnlineGame, type Intent } from './online';
import { GameScreen } from './GameScreen';
import { CODE_LENGTH, normalizeCode } from '@shared/protocol';

type Route = { k: 'home' } | { k: 'lobby'; code?: string } | { k: 'game'; adapter: GameAdapter };

const FOUR_KEY = 'cribbage.fourcolor.v1';
const COUNT_KEY = 'cribbage.countmode.v1';
const NAME_KEY = 'cribbage.name.v1';

function readBool(key: string, d: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? d : v === '1';
  } catch {
    return d;
  }
}
function writeBool(key: string, v: boolean) {
  try {
    localStorage.setItem(key, v ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function App() {
  const joinFromUrl = useMemo(() => {
    const m = /^\/join\/([A-Za-z]+)/.exec(location.pathname);
    return m ? normalizeCode(m[1]!) : undefined;
  }, []);
  // A reopened phone lands back at its table: a stored seat token means an
  // online game may still be waiting. If the server says it is gone, the
  // GameScreen sends us home.
  const [route, setRoute] = useState<Route>(() =>
    joinFromUrl ? { k: 'lobby', code: joinFromUrl } : OnlineGame.hasResumableToken() ? { k: 'game', adapter: new OnlineGame({ kind: 'resume' }) } : { k: 'home' },
  );
  const [fourColor, setFourColor] = useState(() => readBool(FOUR_KEY, false));
  const [countMode, setCountMode] = useState(() => readBool(COUNT_KEY, true));

  useEffect(() => {
    document.documentElement.dataset.fourcolor = fourColor ? '1' : '0';
    writeBool(FOUR_KEY, fourColor);
  }, [fourColor]);
  useEffect(() => writeBool(COUNT_KEY, countMode), [countMode]);

  const start = (adapter: GameAdapter) => setRoute({ k: 'game', adapter });
  const home = () => {
    if (route.k === 'game') route.adapter.close();
    history.replaceState(null, '', '/');
    setRoute({ k: 'home' });
  };

  if (route.k === 'game') return <GameScreen adapter={route.adapter} onExit={home} countMode={countMode} />;
  if (route.k === 'lobby') return <Lobby initialCode={route.code} onBack={home} onStart={(intent) => start(new OnlineGame(intent))} />;

  return (
    <div className="screen home">
      <h1>Cribbage</h1>
      <p className="tag">Two-hand, to 121. Built for one thumb.</p>
      <button type="button" className="mode-btn" onClick={() => start(new LocalGame('solo'))}>
        <span className="glyph" aria-hidden="true">
          ♠
        </span>
        <span>
          <b>Play the computer</b>
          <span>A full game against a sound opponent.</span>
        </span>
      </button>
      <button type="button" className="mode-btn" onClick={() => setRoute({ k: 'lobby' })}>
        <span className="glyph" aria-hidden="true">
          ♥
        </span>
        <span>
          <b>Play a friend online</b>
          <span>Start a table, share a five-letter code.</span>
        </span>
      </button>
      <button type="button" className="mode-btn primary" onClick={() => start(new LocalGame('coach'))}>
        <span className="glyph" aria-hidden="true">
          ♦
        </span>
        <span>
          <b>Play with a coach</b>
          <span>Same game. After each decision, it tells you what the best play was and why.</span>
        </span>
      </button>
      <div className="hint-line">New to cribbage? Start with the coach.</div>
      {OnlineGame.hasResumableToken() ? (
        <button type="button" className="btn secondary" onClick={() => start(new OnlineGame({ kind: 'resume' }))}>
          Rejoin my online table
        </button>
      ) : null}
      <div className="settings">
        <button type="button" className="chip-btn" aria-pressed={fourColor} onClick={() => setFourColor(!fourColor)}>
          Four-colour deck
        </button>
        <button type="button" className="chip-btn" aria-pressed={countMode} onClick={() => setCountMode(!countMode)}>
          Coach: you count
        </button>
      </div>
    </div>
  );
}

function Lobby(props: { initialCode?: string; onBack: () => void; onStart: (i: Intent) => void }) {
  const [name, setName] = useState(() => {
    try {
      return localStorage.getItem(NAME_KEY) ?? '';
    } catch {
      return '';
    }
  });
  const [code, setCode] = useState(props.initialCode ?? '');
  const saveName = () => {
    try {
      localStorage.setItem(NAME_KEY, name.trim());
    } catch {
      /* ignore */
    }
  };
  const ok = normalizeCode(code).length === CODE_LENGTH;
  return (
    <div className="screen lobby">
      <button type="button" className="nav-back" onClick={props.onBack}>
        ‹ Home
      </button>
      <h2>Play a friend</h2>
      <div className="field">
        <label htmlFor="name">Your name (optional)</label>
        <input id="name" value={name} maxLength={20} onChange={(e) => setName(e.target.value)} autoComplete="nickname" />
      </div>
      <button
        type="button"
        className="btn"
        onClick={() => {
          saveName();
          props.onStart({ kind: 'create', name: name.trim() });
        }}
      >
        Start a table
      </button>
      <div className="field">
        <label htmlFor="code">…or enter a friend's code</label>
        <input id="code" className="code" value={code} maxLength={CODE_LENGTH} onChange={(e) => setCode(normalizeCode(e.target.value))} autoCapitalize="characters" autoCorrect="off" spellCheck={false} inputMode="text" />
      </div>
      <button
        type="button"
        className="btn secondary"
        disabled={!ok}
        onClick={() => {
          saveName();
          props.onStart({ kind: 'join', code: normalizeCode(code), name: name.trim() });
        }}
      >
        Join table
      </button>
    </div>
  );
}
